import { useMemo } from 'react';
import * as d3 from 'd3';
import { GroupData, GroupHistogramData } from '../../types/histogram.types';

interface UseHistogramDataOptions {
  shouldRender: boolean;
  allData: number[];
  bins: number;
  orientation: 'vertical' | 'horizontal';
  isGrouped: boolean;
  groupedData?: GroupData[];
  groupMode: 'overlay' | 'stacked' | 'faceted';
  forcedDataRange?: [number, number];
  forcedMaxFrequency?: number;
}

interface HistogramDataResult {
  histData: d3.Bin<number, number>[] | null;
  groupHistData: GroupHistogramData[] | null;
  xDomain: [number, number];
  yDomain: [number, number];
  originalXDomain: [number, number];
  paddedXDomain: [number, number];
}

/**
 * ヒストグラムデータとスケール設定の計算を行うカスタムフック
 */
export const useHistogramData = ({
  shouldRender,
  allData,
  bins,
  orientation,
  isGrouped,
  groupedData,
  groupMode,
  forcedDataRange,
  forcedMaxFrequency
}: UseHistogramDataOptions): HistogramDataResult | null => {
  return useMemo(() => {
    // 遅延レンダリング：shouldRenderがfalseの場合は計算をスキップ
    if (!shouldRender) return null;
    if (!allData || allData.length === 0) return null;

    // 元のデータ範囲を保存（全データから計算、またはforcedDataRangeを使用）
    const originalXDomain = forcedDataRange || (d3.extent(allData) as [number, number]);

    // X軸の表示範囲にパディングを追加（データ範囲の5%を前後に追加）
    const dataRange = originalXDomain[1] - originalXDomain[0];
    const padding = dataRange * 0.05;
    const paddedXDomain: [number, number] = [
      originalXDomain[0] - padding,
      originalXDomain[1] + padding
    ];

    // ヒストグラムデータを計算
    // 等間隔のビンを作成（最大値を確実に含むため、わずかに拡張）
    const histRange = originalXDomain[1] - originalXDomain[0];
    const epsilon = histRange * 0.0001; // データ範囲の0.01%
    const adjustedMax = originalXDomain[1] + epsilon;

    // 等間隔のthresholdsを生成
    const binWidth = (adjustedMax - originalXDomain[0]) / bins;
    const thresholds: number[] = [];
    for (let i = 0; i <= bins; i++) {
      thresholds.push(originalXDomain[0] + i * binWidth);
    }

    const histogram = d3.histogram()
      .value(d => d)
      .domain([originalXDomain[0], adjustedMax])
      .thresholds(thresholds);

    // グループ化されていない場合は単純な計算
    if (!isGrouped) {
      const histData = histogram(allData);

      // Y軸（度数）のスケール
      // forcedMaxFrequencyが指定されている場合はそれを使用（facetedモードでの統一のため）
      const yMax = forcedMaxFrequency !== undefined ? forcedMaxFrequency : (d3.max(histData, d => d.length) || 0);

      return {
        histData,
        groupHistData: null,
        xDomain: orientation === 'horizontal' ? [0, yMax] as [number, number] : paddedXDomain,
        yDomain: orientation === 'horizontal' ? paddedXDomain : [0, yMax] as [number, number],
        originalXDomain,
        paddedXDomain
      };
    } else {
      // グループ化されている場合の処理
      // 各グループのヒストグラムデータを計算
      const groupHistData = groupedData!.map(group => ({
        groupName: group.groupName,
        histogram: histogram(group.values),
        originalData: group.originalData
      }));

      // Y軸（度数）のスケール
      // forcedMaxFrequencyが指定されている場合はそれを使用（facetedモードでの統一のため）
      let yMax = 0;
      if (forcedMaxFrequency !== undefined) {
        yMax = forcedMaxFrequency;
      } else if (groupMode === 'stacked') {
        // stacked modeの場合は積み上げ計算
        const binSums = new Map<string, number>();
        groupHistData.forEach(({ histogram }) => {
          histogram.forEach(bin => {
            const key = `${bin.x0}-${bin.x1}`;
            binSums.set(key, (binSums.get(key) || 0) + bin.length);
          });
        });
        yMax = Math.max(...binSums.values(), 0);
      } else {
        // overlay modeの場合は各グループの最大値を取得
        yMax = Math.max(...groupHistData.map(({ histogram }) =>
          Math.max(...histogram.map(d => d.length), 0)
        ), 0);
      }

      return {
        histData: null,
        groupHistData,
        xDomain: orientation === 'horizontal' ? [0, yMax] as [number, number] : paddedXDomain,
        yDomain: orientation === 'horizontal' ? paddedXDomain : [0, yMax] as [number, number],
        originalXDomain,
        paddedXDomain
      };
    }

  }, [shouldRender, allData, bins, orientation, isGrouped, groupedData, groupMode, forcedDataRange, forcedMaxFrequency]);
};
